'use client';

import { useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { Tabs } from '@zillow/constellation-10';
import { constellationTabBackgroundOverride } from '@/src/styles/constellationTabs.styles';
import { SchoolListItem } from './SchoolListItem';
import { DisclaimerTrigger } from './DisclaimerTrigger';
import {
  buildAllTabOrdering,
  computeNearestSchools,
  type District,
  getShowingMessage,
  groupSchoolsByType,
  INITIAL_VISIBLE_COUNT,
  lookupDistrictTypeLabel,
  type NearbySchool,
  renderDistrictJoiner,
  type School,
  type SchoolTypeId,
} from './schoolUtils';
import {
  container,
  disclaimer,
  districtMessage,
  link,
  schoolList,
  showingMessage,
  showMoreButton,
  srOnly,
  tabPanel,
  tabPanelInactive,
  tabPanelStack,
} from './nearbySchoolsModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

type TabConfig = {
  id: SchoolTypeId;
  label: string;
  schools: Array<NearbySchool>;
  firstRestIndex: number;
};

const getSchoolListItemKey = (tabId: SchoolTypeId, school: NearbySchool, index: number): string =>
  `${tabId}-${school.uriV2 || school.name || school.id || 'school'}-${index}`;

export const NearbySchoolsModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');

  const nearbySchools = useMemo(() => {
    const rawSchools = (listing?.schools ?? []) as Array<School>;
    const listingGeo = listing?.geo;
    if (rawSchools.length === 0 || !listingGeo) return [];
    return computeNearestSchools(rawSchools, listingGeo);
  }, [listing]);

  const grouped = useMemo(() => groupSchoolsByType(nearbySchools), [nearbySchools]);
  const allOrdering = useMemo(() => buildAllTabOrdering(grouped), [grouped]);

  const universitiesFromAreas = useMemo(() => {
    const fromAreas = (listing?.areas?.schools?.university ?? []) as unknown as Array<School>;
    return fromAreas.map<NearbySchool>((school) => ({
      ...school,
      distance: 0,
    }));
  }, [listing]);

  const tabs: Array<TabConfig> = useMemo(() => {
    const result: Array<TabConfig> = [];
    if (nearbySchools.length > 0) {
      result.push({
        id: 'all',
        label: t('schoolTabAll'),
        schools: allOrdering.schools,
        firstRestIndex: allOrdering.firstRestIndex,
      });
    }
    if (grouped.primaryschool.length > 0) {
      result.push({
        id: 'primaryschool',
        label: t('schoolTabPrimary'),
        schools: grouped.primaryschool,
        firstRestIndex: INITIAL_VISIBLE_COUNT,
      });
    }
    if (grouped.middleschool.length > 0) {
      result.push({
        id: 'middleschool',
        label: t('schoolTabMiddle'),
        schools: grouped.middleschool,
        firstRestIndex: INITIAL_VISIBLE_COUNT,
      });
    }
    if (grouped.highschool.length > 0) {
      result.push({
        id: 'highschool',
        label: t('schoolTabHigh'),
        schools: grouped.highschool,
        firstRestIndex: INITIAL_VISIBLE_COUNT,
      });
    }
    if (grouped.mixedschool.length > 0) {
      result.push({
        id: 'mixedschool',
        label: t('schoolTabMixed'),
        schools: grouped.mixedschool,
        firstRestIndex: INITIAL_VISIBLE_COUNT,
      });
    }
    if (universitiesFromAreas.length > 0) {
      result.push({
        id: 'university',
        label: t('schoolTabUniversity'),
        schools: universitiesFromAreas,
        firstRestIndex: INITIAL_VISIBLE_COUNT,
      });
    }
    return result;
  }, [allOrdering, grouped, nearbySchools.length, t, universitiesFromAreas]);

  const [activeTabId, setActiveTabId] = useState<SchoolTypeId>(tabs[0]?.id ?? 'all');
  const [expandedByTab, setExpandedByTab] = useState<Record<SchoolTypeId, boolean>>({
    all: false,
    primaryschool: false,
    middleschool: false,
    highschool: false,
    mixedschool: false,
    university: false,
  });
  const firstExpandedRef = useRef<HTMLLIElement | null>(null);

  const districts = ((listing.areas?.districts ?? []) as Array<District>).filter(
    (district) => district?.uriV2 && district?.name,
  );
  const hasSchools = tabs.length > 0;
  const hasDistricts = districts.length > 0;

  if (!hasSchools && !hasDistricts) return null;

  const displayName = listing.displayName ?? listing.address?.street ?? t('thisListing');
  const hideStreet = Boolean((listing.address as { hideStreet?: boolean } | undefined)?.hideStreet);

  const districtIntro = hasSchools
    ? hideStreet
      ? t('schoolsDistrictIntroNear')
      : t('schoolsDistrictIntroAt', { name: displayName })
    : hideStreet
      ? t('schoolsDistrictIntroNear')
      : t('schoolsDistrictIntroNoSchools', { name: displayName });
  const districtTail = hasSchools ? t('schoolsDistrictTailWithSchools') : '.';

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];

  const handleToggleExpand = (tabId: SchoolTypeId) => {
    setExpandedByTab((prev) => {
      const next = { ...prev, [tabId]: !prev[tabId] };
      if (next[tabId] && !prev[tabId]) {
        requestAnimationFrame(() => {
          firstExpandedRef.current?.focus();
        });
      }
      return next;
    });
  };

  return (
    <div className={container}>
      {hasDistricts && (
        <p className={districtMessage}>
          {districtIntro}{' '}
          {districts.map((district, i) => {
            const typeLabel = lookupDistrictTypeLabel(district.type);
            const suffix = typeLabel && !district.name.includes(typeLabel) ? ` (${typeLabel})` : '';
            return (
              <span key={district.id ?? `${district.name}-${i}`}>
                <a href={district.uriV2} className={link}>
                  {district.name}
                </a>
                {suffix}
                {renderDistrictJoiner(i, districts.length)}
              </span>
            );
          })}
          {districtTail}
        </p>
      )}

      {hasSchools && activeTab && (
        <div>
          {tabs.length > 1 && (
            <Tabs.Root
              selected={activeTab.id}
              onSelectedChange={(value) => setActiveTabId(value as SchoolTypeId)}
              css={constellationTabBackgroundOverride}
            >
              <Tabs.List aria-label={t('filterBySchoolType')}>
                {tabs.map((tab) => (
                  <Tabs.Tab key={tab.id} value={tab.id}>
                    {tab.label}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs.Root>
          )}

          <div className={tabPanelStack}>
            {tabs.map((tab) => {
              const isActive = tab.id === activeTab.id;
              const isExpanded = expandedByTab[tab.id];
              const totalSchools = tab.schools.length;
              const expandable = totalSchools > INITIAL_VISIBLE_COUNT;
              const schoolsToShow = isExpanded ? tab.schools : tab.schools.slice(0, INITIAL_VISIBLE_COUNT);
              const allVisibleCount =
                tab.id === 'all' ? tab.firstRestIndex : Math.min(totalSchools, INITIAL_VISIBLE_COUNT);

              return (
                <div
                  key={tab.id}
                  id={`${tab.id}-panel`}
                  role="tabpanel"
                  aria-labelledby={`${tab.id}-tab`}
                  aria-hidden={!isActive}
                  className={`${tabPanel}${!isActive ? ` ${tabPanelInactive}` : ''}`}
                >
                  <ul className={schoolList}>
                    {schoolsToShow.map((school, i) => {
                      const isFocusTarget = isActive && isExpanded && i === tab.firstRestIndex;
                      return (
                        <SchoolListItem
                          key={getSchoolListItemKey(tab.id, school, i)}
                          school={school}
                          ref={isFocusTarget ? firstExpandedRef : undefined}
                        />
                      );
                    })}
                  </ul>
                  {expandable && !isExpanded && (
                    <p className={showingMessage}>{getShowingMessage(tab.id, totalSchools, allVisibleCount)}</p>
                  )}
                  {expandable && (
                    <button
                      type="button"
                      className={showMoreButton}
                      onClick={() => handleToggleExpand(tab.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`${tab.id}-panel`}
                      tabIndex={isActive ? undefined : -1}
                    >
                      {isExpanded ? t('showLess') : t('showMore')}
                      <span className={srOnly}>{` ${tab.label.toLowerCase()} schools`}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className={disclaimer}>
        {t('greatSchoolsDisclaimer')} <DisclaimerTrigger />
      </p>
    </div>
  );
};
